import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";

import { Dropdown, Form, FormField, FormGroup, Input, Label } from "semantic-ui-react";

import UserProfileImage from "react-lib/apps/IsHealth/REG/UserProfileImage";

import OfficialAccountAddressForm, {
  OfficialAccountAddressDataType,
} from "./OfficialAccountAddressForm";

import BusinessInformationForm from "./BusinessInformationForm";
import AccountingInformationForm from "./AccountingInformationForm";
import AccountingAddressForm from "./AccountingAddressForm";

type InformationDetailedProps = {
  detail?: InformationDetailedDataType;
  error?: Record<string, any>;
  readOnly?: boolean;
  masterData: any;
  masterOptions: any;
  healthFacilityTypeOptions?: Record<string, any>[];
  onChangeLogoImage?: (file: File, fileUrl: string) => void;
  languageUX: "en" | "th";
};

export type InformationDetailedDataType = OfficialAccountAddressDataType &
  Partial<{
    accountName: string;
    address: string;
    description: string;
    fileImage?: File | null;
    hospital_name: string;
    image?: string | null;
    username: string;
    business?: {
      business_type?: string;
      branch_type?: string;
      branch_name?: number;
      first_name?: string;
      last_name?: string;
      business_name?: string;
      tax_payer_id?: string;
    };
    accounting: {
      accounting_business_name?: string;
      accounting_tax_payer_id?: string;
      accounting_address_text?: string;
      accounting_city?: string;
      accounting_district?: string;
      accounting_province?: string;
      accounting_zipcode?: string;
      accounting_image_file?: File | null;
    };
  }>;

const HOSPITAL_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/hospital.png`;

const InformationFormDetailed = forwardRef<any, InformationDetailedProps>((props, ref) => {
  const [detail, setDetail] = useState<InformationDetailedDataType>({});

  useImperativeHandle(ref, () => ({
    getData: (): InformationDetailedDataType => ({
      oa_type: detail.oa_type,
      accountName: detail.accountName,
      address: detail.address,
      description: detail.description,
      division_address: detail.division_address,
      fileImage: detail.fileImage,
      hospital_name: detail.hospital_name,
      image: detail.image,
      has_staff: detail.has_staff,
      business: {
        business_type: detail.business?.business_type,
        branch_type: detail.business?.branch_type,
        branch_name: detail.business?.branch_name,
        first_name: detail.business?.first_name,
        last_name: detail.business?.last_name,
        business_name: detail.business?.business_name,
        tax_payer_id: detail.business?.tax_payer_id,
      },
      accounting: {
        accounting_business_name: detail.accounting?.accounting_business_name,
        accounting_tax_payer_id: detail.accounting?.accounting_tax_payer_id,
        accounting_address_text: detail.accounting?.accounting_address_text || "",
        accounting_city: detail.accounting?.accounting_city || "",
        accounting_district: detail.accounting?.accounting_district || "",
        accounting_province: detail.accounting?.accounting_province || "",
        accounting_zipcode: detail.accounting?.accounting_zipcode || "",
        accounting_image_file: detail.accounting?.accounting_image_file || null,
      },
    }),
  }));

  useEffect(() => {
    if (props.detail) {
      setDetail(props.detail);
    }
  }, [props.detail]);

  const handleChangeValue = (e: any, data: any) => {
    const name: string = data.name || "";
    const value = data.type === "checkbox" ? data.checked : data.value;

    const [first, second] = name.split(".");

    let secondData = {
      [second]: value,
    };

    if (second === "branch_type") {
      secondData = {
        ...secondData,
        branch_name: "",
      };
    }

    setDetail((prev) => ({
      ...prev,
      [first]: second
        ? {
            ...(prev as any)[first],
            ...secondData,
          }
        : value,
    }));
  };

  const handleImageChange = (file: File, fileUrl: string) => {
    setDetail((prev) => ({
      ...prev,
      fileImage: file,
      image: fileUrl,
    }));
  };

  const handleClearImage = () => {
    setDetail((prev) => ({
      ...prev,
      fileImage: null,
      image: props.detail?.image,
    }));
  };

  const requiredTextError = (isRequired: boolean) => {
    if (isRequired)
      return (
        <label style={{ color: "red", fontSize: "12px", fontWeight: "normal" }}>
          <span style={{ color: "red" }}>*</span>
          {props.languageUX === "en"
            ? "Please complete all required fields."
            : "กรุณาระบุข้อมูลให้ครบถ้วนค่ะ"}
        </label>
      );
  };

  return (
    <div className="information-form-detailed">
      <div>
        <div style={{ marginBottom: "1rem" }}>
          <Label style={{ backgroundColor: "var(--light-bg-color)", width: "100%" }} size="large">
            ข้อมูลเข้าสู่ระบบ
          </Label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <Label style={{ backgroundColor: "var(--light-bg-color)", width: "100%" }} size="large">
            ข้อมูลบัญชี
          </Label>
        </div>
        <UserProfileImage
          src={detail.image || HOSPITAL_IMAGE_URL}
          onChange={handleImageChange}
          onClear={handleClearImage}
        />
      </div>
      <div>
        <div>
          <Label style={{ minWidth: "20rem" }} size="large">
            {props.detail?.username || "\u00A0"}
          </Label>
        </div>
        <Form className="--override-disabled --grey" style={{ marginTop: "5rem" }}>
          <FormGroup inline>
            <FormField style={{ alignItems: "baseline" }} width={11} inline required>
              <label>ชื่อ Official Account</label>

              <div style={{ width: "100%" }}>
                <Input
                  error={props.error?.accountName || ""}
                  name="accountName"
                  placeholder="กรุณาระบุชื่อ"
                  readOnly={props.readOnly}
                  value={detail.accountName}
                  fluid
                  onChange={handleChangeValue}
                />
                {requiredTextError(props.error?.accountName)}
              </div>
            </FormField>
          </FormGroup>
          <FormGroup inline>
            <FormField style={{ alignItems: "baseline" }} width={11} inline required>
              <label>ประเภทธุรกิจ</label>
              <div style={{ width: "100%" }}>
                <Dropdown
                  style={{ width: "100%" }}
                  disabled={props.readOnly}
                  error={props.error?.hospital_name || ""}
                  name="hospital_name"
                  placeholder="-- กรุณาเลือกประเภทธุรกิจ --"
                  value={detail.hospital_name}
                  fluid
                  search
                  selection
                  options={props.healthFacilityTypeOptions}
                  onChange={handleChangeValue}
                />
                {requiredTextError(props.error?.accountName)}
              </div>
            </FormField>
          </FormGroup>
          <BusinessInformationForm
            detail={detail}
            readOnly={props.readOnly ?? false}
            handleChangeValue={handleChangeValue}
            error={props.error}
            businessTypeWidth={16}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />
          <OfficialAccountAddressForm
            data={detail}
            error={props.error}
            readOnly={props.readOnly}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            onChangeValue={handleChangeValue}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />
          {/* <FormGroup inline>
            <FormField width={11} inline>
              <label>ตำแหน่งสถานที่ตั้ง</label>
              <Input
                name="address"
                placeholder="กรุณาระบุสถานที่ตั้ง"
                readOnly={props.readOnly}
                value={detail.address}
                onChange={handleChange}
              />
            </FormField>
          </FormGroup> */}
          <FormGroup inline>
            <FormField width={11} inline>
              <label>รายละเอียดเพิ่มเติม</label>
              <Input
                name="description"
                placeholder="กรุณาระบุรายละเอียด"
                readOnly={props.readOnly}
                value={detail.description}
                fluid
                onChange={handleChangeValue}
              />
            </FormField>
          </FormGroup>
          <Label
            style={{
              backgroundColor: "var(--light-bg-color)",
              margin: "1.25rem 0 1rem",
              width: "35.5%",
            }}
            size="large"
          >
            ข้อมูลสำหรับออกใบกำกับภาษี
          </Label>
          <AccountingInformationForm
            detail={detail}
            readOnly={props.readOnly ?? false}
            handleChangeValue={handleChangeValue}
            error={props.error}
            businessTypeWidth={16}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />

          <AccountingAddressForm
            data={detail}
            readOnly={props.readOnly}
            error={props.error}
            masterData={props.masterData}
            masterOptions={props.masterOptions}
            onChangeValue={handleChangeValue}
            onChangeLogoImage={props.onChangeLogoImage}
            requiredTextError={requiredTextError}
            languageUX={props.languageUX}
          />
        </Form>
      </div>
    </div>
  );
});

InformationFormDetailed.displayName = "InformationFormDetailed";

export default React.memo(InformationFormDetailed);
